import { AlertCircle, Bell, Check, Info, X } from 'lucide-react'
import { AnimatePresence, motion, type PanInfo, type Transition, useReducedMotion } from 'motion/react'
import { type CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { type ParsleyToast, pauseToastTimers, resumeToastTimers, ToastStatus, toast, useToasts } from '../lib/toast-store'
import { useIsMobile } from '../lib/useIsMobile'
import { cn } from '../lib/utils'

/** Where the stack anchors. Parsley uses `bottom-right` on desktop, `bottom-center` on mobile. */
export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

/** Per-slot class overrides, so a caller can tune the surface without forking the component. */
export interface ToastClassNames {
  root?: string
  item?: string
  surface?: string
  iconWrap?: string
  content?: string
  title?: string
  description?: string
  action?: string
  close?: string
}

// Spring that settles the stack — weighty but quick, tuned to the app's other motion.
const STACK_SPRING: Transition = { type: 'spring', stiffness: 420, damping: 34, mass: 0.75 }
// Expo-out — the design system's standard reveal ease, reused for the exit slide.
const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1]
// Past this drag distance (px) or fling velocity (px/s), a horizontal swipe dismisses.
const SWIPE_DISTANCE = 72
const SWIPE_VELOCITY = 520

/**
 * Two surfaces. `inverse` (the default) is the opposite of the theme: deep green in light mode, near-white
 * in dark. Its light hairline carries the edge where the fill can't — over the green footer. `surface` is
 * the neutral card, which barely separates from the page; keep it for toasts shown inside a card.
 */
export type ToastTone = 'surface' | 'inverse'

interface ToneStyles {
  surface: string
  title: string
  description: string
  action: string
  close: string
  ring: string
}

const TONE: Record<ToastTone, ToneStyles> = {
  surface: {
    surface: 'border-border bg-card shadow-float',
    title: 'text-foreground',
    description: 'text-muted-foreground',
    action: 'bg-foreground/[0.06] text-foreground hover:bg-foreground/[0.1]',
    close: 'text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground',
    ring: 'focus-visible:ring-pelorous-500 focus-visible:ring-offset-card dark:focus-visible:ring-pelorous-300'
  },
  inverse: {
    surface: 'border-gray-200 bg-brand-green-900 shadow-deep-shadow dark:bg-gray-50',
    title: 'text-white dark:text-gray-950',
    description: 'text-white/70 dark:text-gray-700',
    action: 'bg-white/15 text-white hover:bg-white/25 dark:bg-gray-950/10 dark:text-gray-950 dark:hover:bg-gray-950/15',
    close: 'text-white/75 hover:bg-white/15 hover:text-white dark:text-gray-700 dark:hover:bg-gray-950/10 dark:hover:text-gray-950',
    ring: 'focus-visible:ring-white/80 focus-visible:ring-offset-brand-green-900 dark:focus-visible:ring-pelorous-600 dark:focus-visible:ring-offset-gray-50'
  }
}

// The status chip, per tone. On the neutral `surface` each chip is a confident status-coloured fill
// with a white glyph (success in brand green is the Parsley moment; neutral is a quiet grey). On the
// `inverse` in light mode the chips go translucent-white so they read on green — except success, which
// lights up as a bright-green badge, and error, which stays rose. On its dark-mode light fill they go solid.
const STATUS_CHIP: Record<ToastTone, Record<ToastStatus, string>> = {
  surface: {
    [ToastStatus.Neutral]: 'bg-gray-400 text-white',
    [ToastStatus.Info]: 'bg-pelorous-500 text-white',
    [ToastStatus.Loading]: 'bg-brand-green-600 text-white dark:bg-brand-green-600',
    [ToastStatus.Success]: 'bg-brand-green-600 text-white dark:bg-brand-green-600',
    [ToastStatus.Error]: 'bg-red text-white'
  },
  inverse: {
    [ToastStatus.Neutral]: 'bg-white/15 text-white dark:bg-gray-600',
    [ToastStatus.Info]: 'bg-white/15 text-white dark:bg-pelorous-600',
    [ToastStatus.Loading]: 'bg-white/15 text-white dark:bg-brand-green-600',
    [ToastStatus.Success]: 'bg-brand-green-400 text-brand-green-950 dark:bg-brand-green-600 dark:text-white',
    [ToastStatus.Error]: 'bg-red text-white dark:bg-red-600'
  }
}

/** The status glyph. `loading` is a spinning ring; the rest are lucide icons. */
function StatusGlyph({ status }: { status: ToastStatus }) {
  const reduced = useReducedMotion()

  if (status === ToastStatus.Loading) {
    return (
      <motion.span
        aria-hidden
        className="block h-[15px] w-[15px] rounded-full border-2 border-white/40 border-t-white"
        animate={{ rotate: 360 }}
        transition={{ duration: reduced ? 2.4 : 0.9, ease: 'linear', repeat: Number.POSITIVE_INFINITY }}
      />
    )
  }

  const Icon =
    status === ToastStatus.Success ? Check : status === ToastStatus.Error ? AlertCircle : status === ToastStatus.Info ? Info : Bell
  return <Icon className="h-4 w-4" strokeWidth={2.2} />
}

export interface ToastProps {
  toast: ParsleyToast
  /** Visual tone: the theme-inverted `inverse` (default) or the neutral `surface` card. */
  tone?: ToastTone
  /** Stack index from the top; drives z-order so newer toasts sit above older. */
  index?: number
  onDismiss?: (id: string) => void
  /** Fired when the pointer enters or a drag begins — used to pause auto-dismiss. */
  onInteractStart?: () => void
  /** Fired when the pointer leaves or a drag ends — used to resume auto-dismiss. */
  onInteractEnd?: () => void
  classNames?: ToastClassNames
}

/**
 * A single toast surface: an elevated card (theme-inverted `inverse` or neutral `surface`) with a status
 * chip, message, optional action, and close/swipe dismissal. Presentational — it holds no timer; the
 * store owns auto-dismiss. Rendered by {@link Toaster}, but exported so it can be shown statically
 * (e.g. in Storybook). Enters with a spring-blur rise; a horizontal drag flings it away.
 */
export function Toast({ toast: item, tone = 'inverse', index = 0, onDismiss, onInteractStart, onInteractEnd, classNames }: ToastProps) {
  const reduced = useReducedMotion()
  const { id, title, description, status, action, dismissible, icon } = item
  const canDismiss = dismissible && Boolean(onDismiss)
  const styles = TONE[tone]
  // A title-only toast is one line — centre it against the chip. With a description or an action the
  // content is multi-row, so top-align instead and the chip tracks the title rather than the block's centre.
  const singleLine = !description && !action

  function handleDragEnd(_event: unknown, info: PanInfo) {
    onInteractEnd?.()
    if (canDismiss && (Math.abs(info.offset.x) > SWIPE_DISTANCE || Math.abs(info.velocity.x) > SWIPE_VELOCITY)) {
      onDismiss?.(id)
    }
  }

  return (
    <motion.li
      layout
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.96, filter: 'blur(10px)' }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      exit={
        reduced ? { opacity: 0 } : { opacity: 0, x: 40, scale: 0.96, filter: 'blur(8px)', transition: { duration: 0.18, ease: EASE_OUT } }
      }
      transition={STACK_SPRING}
      drag={canDismiss && !reduced ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragStart={onInteractStart}
      onDragEnd={handleDragEnd}
      onHoverStart={onInteractStart}
      onHoverEnd={onInteractEnd}
      style={{ zIndex: 40 - index }}
      className={cn('pointer-events-auto relative w-full will-change-transform', classNames?.item)}>
      <div
        className={cn(
          'flex gap-3 rounded-2xl border p-3.5 pr-2.5',
          singleLine ? 'items-center' : 'items-start',
          styles.surface,
          classNames?.surface
        )}>
        <span
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            !singleLine && 'mt-px',
            STATUS_CHIP[tone][status],
            classNames?.iconWrap
          )}>
          {icon ?? <StatusGlyph status={status} />}
        </span>

        <div className={cn('min-w-0 flex-1', !singleLine && 'pt-0.5', classNames?.content)}>
          <p className={cn('text-sm font-semibold leading-5', styles.title, classNames?.title)}>{title}</p>
          {description ? <p className={cn('mt-0.5 text-xs', styles.description, classNames?.description)}>{description}</p> : null}
          {action ? (
            <button
              type="button"
              onClick={() => {
                action.onClick(id)
                onDismiss?.(id)
              }}
              className={cn(
                'mt-2 inline-flex h-8 items-center rounded-full px-3.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-2',
                styles.action,
                styles.ring,
                classNames?.action
              )}>
              {action.label}
            </button>
          ) : null}
        </div>

        {canDismiss ? (
          <button
            type="button"
            onClick={() => onDismiss?.(id)}
            aria-label="Dismiss"
            className={cn(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-2',
              !singleLine && 'mt-px',
              styles.close,
              styles.ring,
              classNames?.close
            )}>
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>
        ) : null}
      </div>
    </motion.li>
  )
}

/** A pixel value (`24` → `24px`) or a raw CSS length (`'calc(env(safe-area-inset-bottom) + 5rem)'`). */
type Inset = number | string
type InsetEdges = Partial<Record<'top' | 'bottom' | 'left' | 'right', Inset>>

export interface ToasterProps {
  /** Anchor at the `md` breakpoint and up. */
  position?: ToastPosition
  /** Anchor below `md`. Defaults to `bottom-center`. */
  mobilePosition?: ToastPosition
  /** Distance from the anchored edges (desktop). Unspecified edges fall back to 24px. */
  offset?: InsetEdges
  /** Distance from the anchored edges (mobile). Apps with bottom chrome (a tab bar) should lift `bottom` here. */
  mobileOffset?: InsetEdges
  /** How many toasts render at once; extras stay queued in the store. */
  maxVisible?: number
  /** Gap between stacked toasts, in px. */
  gap?: number
  className?: string
  classNames?: ToastClassNames
  /** Visual tone, the same at every width. Defaults to `inverse`, which flips with the theme. */
  tone?: ToastTone
  /** Render into a `document.body` portal (a fixed viewport overlay). Defaults to true. */
  portal?: boolean
}

const DEFAULT_EDGE = 24

function toCss(value: Inset | undefined, fallback: number): string {
  if (value === undefined) return `${fallback}px`
  return typeof value === 'number' ? `${value}px` : value
}

/**
 * The mounted toast viewport. Subscribe-and-render only — call `toast.*` (from the store) to add
 * toasts anywhere. Anchors responsively (`position` on desktop, `mobilePosition` below `md`) and
 * pauses auto-dismiss while the stack is hovered. Mount once, near the app root.
 */
export function Toaster({
  position = 'bottom-right',
  mobilePosition = 'bottom-center',
  offset,
  mobileOffset,
  maxVisible = 3,
  gap = 12,
  className,
  classNames,
  tone = 'inverse',
  portal = true
}: ToasterProps) {
  const isMobile = useIsMobile()
  const toasts = useToasts()
  // Portal target only exists after mount (SSR-safe, and document.body is ready).
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const pos = isMobile ? mobilePosition : position
  const insets = (isMobile ? mobileOffset : offset) ?? {}

  const isBottom = pos.startsWith('bottom')
  const isTop = pos.startsWith('top')
  const isCenter = pos.endsWith('center')
  const isLeft = pos.endsWith('left')
  const isRight = pos.endsWith('right')

  const style: CSSProperties = {
    top: isTop ? toCss(insets.top, DEFAULT_EDGE) : undefined,
    bottom: isBottom ? toCss(insets.bottom, DEFAULT_EDGE) : undefined,
    left: isCenter ? '50%' : isLeft ? toCss(insets.left, DEFAULT_EDGE) : undefined,
    right: isRight ? toCss(insets.right, DEFAULT_EDGE) : undefined,
    transform: isCenter ? 'translateX(-50%)' : undefined,
    gap: `${gap}px`
  }

  const visible = toasts.slice(-maxVisible)

  const stack = (
    <ol
      aria-live="polite"
      aria-atomic="false"
      style={style}
      className={cn(
        'pointer-events-none fixed z-[100] flex w-[calc(100vw-2rem)] max-w-[400px] flex-col',
        isBottom && 'flex-col-reverse',
        classNames?.root,
        className
      )}>
      <AnimatePresence initial={false} mode="popLayout">
        {visible.map((item, i) => (
          <Toast
            key={item.id}
            toast={item}
            tone={tone}
            index={visible.length - 1 - i}
            onDismiss={toast.dismiss}
            onInteractStart={pauseToastTimers}
            onInteractEnd={resumeToastTimers}
            classNames={classNames}
          />
        ))}
      </AnimatePresence>
    </ol>
  )

  if (!portal) return stack
  if (!mounted) return null
  return createPortal(stack, document.body)
}
